// Vue Router Configuration with Authentication Guards
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthenticationStore } from '@/stores/authenticationStore';
import { useClientStore } from '@/stores/clientStore';
import { useEmployeeStore } from '@/stores/employeeStore';
import { PrivilegesConstants } from '@/constants/privilegesConstants';

const routes: RouteRecordRaw[] = [
  // Public Routes
  {
    path: '/',
    name: 'Home',
    component: () => import('@/components/public/PublicServiceGrid.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/book/:serviceId',
    name: 'public-booking',
    component: () => import('@/components/public/PublicBookingFlow.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/payment/return',
    name: 'PaymentReturn',
    component: () => import('@/components/public/PaymentReturn.vue'),
    meta: { requiresAuth: false }
  },
  
  // Authentication Routes
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/components/auth/Login.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/components/auth/ForgotPassword.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/components/auth/ResetPassword.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/components/auth/ClientRegistration.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: () => import('@/components/auth/EmailVerification.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/complete-registration',
    name: 'CompleteRegistration',
    component: () => import('@/components/clientInvitation/CompleteRegistration.vue'),
    meta: { requiresAuth: false }
  },
  
  // Authenticated Routes
  {
    path: '/app',
    name: 'Dashboard',
    component: () => import('@/components/dashboard/Dashboard.vue'),
    meta: { 
      requiresAuth: true,
      breadcrumb: [{ label: 'Dashboard' }]
    }
  },
  {
    path: '/waitlist/invites/:bookingId',
    name: 'WaitlistInvites',
    component: () => import('@/components/waitlist/WaitlistInvitesPanel.vue'),
    meta: {
      requiresAuth: true,
      requiresPrivilege: PrivilegesConstants.ViewWaitlist,
      breadcrumb: [{ label: 'Waitlist' }, { label: 'Invites' }]
    }
  },
  {
    path: '/waitlist',
    name: 'Waitlist',
    component: () => import('@/components/waitlist/WaitList.vue'),
    meta: {
      requiresAuth: true,
      requiresPrivilege: PrivilegesConstants.ViewWaitlist,
      breadcrumb: [{ label: 'Waitlist' }]
    }
  },
  {
    path: '/my-schedule',
    name: 'MySchedule',
    component: () => import('@/components/employee/MySchedule.vue'),
    meta: {
      requiresAuth: true,
      breadcrumb: [{ label: 'MySchedule' }]
    }
  },
  {
    path: '/my-absences',
    name: 'MyAbsences',
    component: () => import('@/components/employee/MyAbsences.vue'),
    meta: {
      requiresAuth: true,
      breadcrumb: [{ label: 'MyAbsences' }]
    }
  },
  {
    path: '/my-bookings',
    name: 'MyBookings',
    component: () => import('@/components/employee/MyBookings.vue'),
    meta: {
      requiresAuth: true,
      breadcrumb: [{ label: 'MyBookings' }]
    }
  },
  {
    path: '/my-profile',
    name: 'MyProfile',
    component: () => import('@/components/client/MyProfile.vue'),
    meta: {
      requiresAuth: true,
      breadcrumb: [{ label: 'MyProfile' }]
    }
  },
  {
    path: '/my-client-bookings',
    name: 'MyClientBookings',
    component: () => import('@/components/client/MyBookings.vue'),
    meta: {
      requiresAuth: true,
      breadcrumb: [{ label: 'MyBookings' }]
    }
  },
  {
    path: '/my-passes',
    name: 'MyPasses',
    component: () => import('@/components/client/MyPasses.vue'),
    meta: {
      requiresAuth: true,
      breadcrumb: [{ label: 'MyPasses' }]
    }
  },
  {
    path: '/bookings',
    name: 'Bookings',
    component: () => import('@/components/booking/BookingCalendar.vue'),
    meta: {
      requiresAuth: true,
      requiresPrivilege: PrivilegesConstants.ViewBookings,
      breadcrumb: [{ label: 'Bookings' }]
    }
  },
  {
    path: '/clients',
    name: 'Clients',
    component: () => import('@/components/client/ClientList.vue'),
    meta: {
      requiresAuth: true,
      requiresPrivilege: PrivilegesConstants.ViewClients,
      breadcrumb: [{ label: 'Clients' }]
    }
  },
  {
    path: '/clients/new',
    name: 'ClientCreate',
    component: () => import('@/components/client/ClientDetail.vue'),
    props: { isCreateMode: true },
    meta: {
      requiresAuth: true,
      requiresPrivilege: PrivilegesConstants.CreateClients,
      breadcrumb: [
        { label: 'Clients', to: '/clients' },
        { label: 'NewClient' }
      ]
    }
  },
  {
    path: '/clients/:id',
    name: 'ClientDetail',
    component: () => import('@/components/client/ClientDetail.vue'),
    props: { isCreateMode: false },
    meta: {
      requiresAuth: true,
      requiresPrivilege: PrivilegesConstants.ViewClients,
      breadcrumb: [
        { label: 'Clients', to: '/clients' },
        { label: 'ClientDetail' }
      ]
    }
  },
  {
    path: '/employees',
    name: 'employees',
    component: () => import('@/components/employee/EmployeeList.vue'),
    meta: {
      requiresAuth: true,
      requiresPrivilege: PrivilegesConstants.ViewEmployees,
      breadcrumb: [{ label: 'Employees' }]
    }
  },
  {
    path: '/employees/create',
    name: 'employee-create',
    component: () => import('@/components/employee/EmployeeDetail.vue'),
    meta: {
      requiresAuth: true,
      requiresPrivilege: PrivilegesConstants.CreateEmployees,
      breadcrumb: [
        { label: 'Employees', to: '/employees' },
        { label: 'CreateEmployee' }
      ]
    }
  },
  {
    path: '/employees/:id',
    name: 'employee-detail',
    component: () => import('@/components/employee/EmployeeDetail.vue'),
    meta: {
      requiresAuth: true,
      requiresPrivilege: PrivilegesConstants.ViewEmployees,
      breadcrumb: [
        { label: 'Employees', to: '/employees' },
        { label: 'EmployeeDetail' }
      ]
    }
  },
  {
    path: '/suggestions',
    name: 'Suggestions',
    component: () => import('@/components/suggestions/SuggestionList.vue'),
    meta: {
      requiresAuth: true,
      requiresPrivilege: PrivilegesConstants.ViewSuggestions,
      breadcrumb: [{ label: 'Suggestions' }]
    }
  },
  {
    path: '/my-suggestions',
    name: 'MySuggestions',
    component: () => import('@/components/suggestions/MySuggestions.vue'),
    meta: {
      requiresAuth: true,
      requiresPrivilege: PrivilegesConstants.ViewSuggestions,
      breadcrumb: [{ label: 'MySuggestions' }]
    }
  },
  {
    path: '/notifications',
    name: 'NotificationManagement',
    component: () => import('@/components/notification/NotificationManagement.vue'),
    meta: {
      requiresAuth: true,
      requiresPrivilege: PrivilegesConstants.ViewAllNotifications,
      breadcrumb: [{ label: 'NotificationManagement' }]
    }
  },
  {
    path: '/reports',
    name: 'Reports',
    component: () => import('@/components/report/Reports.vue'),
    meta: {
      requiresAuth: true,
      requiresPrivilege: PrivilegesConstants.ViewReports,
      breadcrumb: [{ label: 'Reports' }]
    }
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('@/components/settings/Settings.vue'),
    meta: { 
      requiresAuth: true,
      requiresPrivilege: PrivilegesConstants.ManageSystemSettings,
      breadcrumb: [{ label: 'Settings' }]
    },
    children: [
      {
        path: 'lookups',
        name: 'Lookups',
        component: () => import('@/components/settings/LookupList.vue'),
        meta: { 
          requiresPrivilege: PrivilegesConstants.ViewLookups,
          breadcrumb: [{ label: 'Settings' }, { label: 'Lookups' }]
        }
      }
    ]
  },
  {
    path: '/service-passes',
    name: 'ServicePasses',
    component: () => import('@/components/servicePass/ServicePassList.vue'),
    meta: {
      requiresAuth: true,
      requiresPrivilege: PrivilegesConstants.ViewServicePasses,
      breadcrumb: [{ label: 'ServicePasses' }]
    }
  },
  /*
  {
    path: '/employees',
    name: 'Employees',
    component: () => import('@/components/employee/EmployeeList.vue'),
    meta: {
      requiresAuth: true,
      requiresPrivilege: PrivilegesConstants.ViewEmployees,
      breadcrumb: [{ label: 'Employees' }]
    }
  },
  {
    path: '/reports',
    name: 'Reports',
    component: () => import('@/components/report/Reports.vue'),
    meta: {
      requiresAuth: true,
      requiresPrivilege: PrivilegesConstants.ViewReports,
      breadcrumb: [{ label: 'Reports' }]
    }
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('@/components/settings/Settings.vue'),
    meta: { 
      requiresAuth: true,
      breadcrumb: [{ label: 'Settings' }]
    },
    children: [
      {
        path: 'lookups',
        name: 'Lookups',
        component: () => import('@/components/settings/LookupList.vue'),
        meta: { 
          requiresPrivilege: PrivilegesConstants.ManageLookups,
          breadcrumb: [{ label: 'Settings' }, { label: 'Lookups' }]
        }
      }
    ]
  },
  */
  {
    path: '/rooms',
    name: 'Rooms',
    component: () => import('@/components/room/RoomList.vue'),
    meta: {
      requiresAuth: true,
      requiresPrivilege: PrivilegesConstants.ViewRooms,
      breadcrumb: [{ label: 'Rooms' }]
    }
  },
  {
    path: '/security/sessions',
    name: 'ActiveSessions',
    component: () => import('@/components/security/ActiveSessions.vue'),
    meta: {
      requiresAuth: true,
      breadcrumb: [{ label: 'Security' }, { label: 'Active Sessions' }]
    }
  },
  {
    path: '/security/login-attempts',
    name: 'LoginAttempts',
    component: () => import('@/components/security/LoginAttempts.vue'),
    meta: {
      requiresAuth: true,
      breadcrumb: [{ label: 'Security' }, { label: 'Login Attempts' }]
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/components/common/NotFound.vue'),
    meta: { requiresAuth: false }
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

// Navigation Guard - Authentication, Authorization, and Breadcrumbs
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthenticationStore();
  
  // Check authentication from localStorage directly (more reliable than computed property)
  const isLoggedIn = !!localStorage.getItem('ruumi_auth_user');

  // Update breadcrumbs dynamically based on route and store data
  if (to.name === 'ClientDetail' && to.params.id) {
    const clientStore = useClientStore();
    if (clientStore.currentClient) {
      const client = clientStore.currentClient;
      to.meta.breadcrumb = [
        { label: 'Clients', to: '/clients', translate: true },
        { label: `${client.firstName} ${client.lastName}`, translate: false }
      ];
    }
  } else if (to.name === 'employee-detail' && to.params.id) {
    const employeeStore = useEmployeeStore();
    if (employeeStore.currentEmployee) {
      const employee = employeeStore.currentEmployee;
      to.meta.breadcrumb = [
        { label: 'Employees', to: '/employees', translate: true },
        { label: `${employee.firstName} ${employee.lastName}`, translate: false }
      ];
    }
  }

  // Public routes
  if (!to.meta.requiresAuth) {
    // If logged in and trying to access login, redirect to dashboard
    if (isLoggedIn && to.path === '/login') {
      return next({ path: '/app' });
    }
    // If logged in and trying to access home, redirect to dashboard ONLY if coming from outside the app (not from dashboard button)
    if (isLoggedIn && to.path === '/' && from.path === '/') {
      return next({ path: '/app' });
    }
    return next();
  }

  // Protected routes require authentication
  if (!isLoggedIn) {
    return next({
      path: '/login',
      query: { redirect: to.fullPath }
    });
  }

  // Check privilege requirements
  if (to.meta.requiresPrivilege) {
    const requiredPrivilege = to.meta.requiresPrivilege as string;
    
    if (!authStore.hasPrivilege(requiredPrivilege)) {
      return next({
        path: '/',
        query: {
          error: 'insufficient_privileges',
          attempted: to.path
        }
      });
    }
  }

  // All checks passed
  next();
});

export default router;
